import { Check, CircleAlert, Hourglass, Keyboard, Landmark, Lock } from 'lucide-preact'
import { text } from '#contract'
import type { LocalizedText, WelfareView } from '#contract'
import { useClient } from '../../../app/client-context.ts'
import { useView } from '../../../app/use-view.ts'
import { dayText } from '../../../i18n/dates.ts'
import { useLocale, useT } from '../../../i18n/index.ts'
import { formatMoney } from '../../../i18n/money.ts'
import { formatOsTime, gameDay, gameNow, timeOfDay } from '../../clock.ts'
import styles from './wellfare.module.css'

/** The parts of an application, in order: `os.app.wellfare.part.<n>.title` / `.detail`. */
const PART_KEYS = ['existence', 'income', 'diary'] as const

/**
 * Wellfare (a Kela parody), on the computer only: this week's application, a part at a time.
 * Each part is one button: the screen fades out to the sound of typing, four hours pass, and
 * it fades back in. Shows the deadline, the payday and how last week went.
 */
export function WellfareApp() {
  const t = useT()
  const welfare = useView((view) => view.welfare)
  return (
    <div class={styles.app}>
      <header class={styles.bar}>
        <Landmark aria-hidden="true" />
        <span class={styles.brand}>{t(text('os.app.wellfare.name'))}</span>
        <span class={styles.motto}>{t(text('os.app.wellfare.motto'))}</span>
      </header>
      {welfare ? <Application welfare={welfare} /> : <Closed />}
    </div>
  )
}

function Closed() {
  const t = useT()
  return (
    <div class={styles.closed}>
      <CircleAlert aria-hidden="true" />
      <p class={styles.closedTitle}>{t(text('os.app.wellfare.closed'))}</p>
      <p>{t(text('os.app.wellfare.empty'))}</p>
    </div>
  )
}

function Application({ welfare }: { readonly welfare: WelfareView }) {
  const t = useT()
  const locale = useLocale()
  const wallet = useView((view) => view.wallet)
  const now = useView((view) => gameNow(view.os.day, view.os.time))
  const money = (cents: number): string => formatMoney(cents, wallet?.currency ?? 'EUR', locale)
  const complete = welfare.parts >= welfare.required
  const when = (at: number): string =>
    `${t(dayText(locale, gameDay(at)))} ${formatOsTime(timeOfDay(at))}`

  return (
    <div class={styles.page}>
      <section class={styles.hero}>
        <p class={styles.eyebrow}>{t(text('os.app.wellfare.benefit'))}</p>
        <p class={styles.amount}>
          {t(text('os.app.wellfare.perWeek', { money: money(welfare.weeklyCents) }))}
        </p>
        <p class={styles.fine}>{t(text('os.app.wellfare.terms'))}</p>
      </section>

      <section class={styles.card}>
        <div class={styles.cardHead}>
          <h2 class={styles.heading}>{t(text('os.app.wellfare.week', { week: welfare.week }))}</h2>
          <span class={styles.pill} data-complete={complete || undefined}>
            {t(
              text('os.app.wellfare.progress', {
                parts: welfare.parts,
                required: welfare.required,
              }),
            )}
          </span>
        </div>
        <div class={styles.meter} aria-hidden="true">
          {Array.from({ length: welfare.required }, (_, i) => (
            <span key={i} class={styles.segment} data-done={i < welfare.parts || undefined} />
          ))}
        </div>
        <dl class={styles.dates}>
          <dt>{t(text('os.app.wellfare.deadline'))}</dt>
          <dd>
            {when(welfare.deadline - 1)}
            {now !== null && !complete && <Left minutes={welfare.deadline - now} />}
          </dd>
          <dt>{t(text('os.app.wellfare.payday'))}</dt>
          <dd>{when(welfare.payday)}</dd>
        </dl>
        <ol class={styles.parts}>
          {Array.from({ length: welfare.required }, (_, i) => (
            <Part key={i} index={i} welfare={welfare} />
          ))}
        </ol>
        {complete ? (
          <p class={styles.done}>
            <Check aria-hidden="true" />
            {t(text('os.app.wellfare.complete', { money: money(welfare.weeklyCents) }))}
          </p>
        ) : (
          <ApplyButton welfare={welfare} />
        )}
      </section>

      {welfare.last && (
        <p class={styles.last} data-paid={welfare.last.paid || undefined}>
          {t(
            welfare.last.paid
              ? text('os.app.wellfare.lastPaid', {
                  week: welfare.last.week,
                  money: money(welfare.weeklyCents),
                })
              : text('os.app.wellfare.lastRefused', { week: welfare.last.week }),
          )}
        </p>
      )}
      <p class={styles.footer}>{t(text('os.app.wellfare.footer'))}</p>
    </div>
  )
}

/** "1 d 5 h left", "3 h left", rounded down to the hour; under an hour, "< 1 h left". */
function Left({ minutes }: { readonly minutes: number }) {
  const t = useT()
  const hours = Math.floor(minutes / 60)
  const label: LocalizedText =
    hours >= 24
      ? text('os.app.wellfare.leftDays', { d: Math.floor(hours / 24), h: hours % 24 })
      : hours >= 1
        ? text('os.app.wellfare.leftHours', { h: hours })
        : text('os.app.wellfare.leftSoon')
  return <span class={styles.left}>{t(label)}</span>
}

function Part({ index, welfare }: { readonly index: number; readonly welfare: WelfareView }) {
  const t = useT()
  const key = PART_KEYS[index] ?? PART_KEYS[0]
  const state = index < welfare.parts ? 'done' : index === welfare.parts ? 'next' : 'locked'
  return (
    <li class={styles.part} data-state={state}>
      <span class={styles.badge} aria-hidden="true">
        {state === 'done' ? <Check /> : state === 'locked' ? <Lock /> : index + 1}
      </span>
      <span class={styles.partText}>
        <span class={styles.partTitle}>{t(text(`os.app.wellfare.part.${key}.title`))}</span>
        <span class={styles.partDetail}>{t(text(`os.app.wellfare.part.${key}.detail`))}</span>
      </span>
      <span class={styles.partState}>
        {t(
          state === 'done'
            ? text('os.app.wellfare.filed')
            : text('os.app.wellfare.hours', { h: welfare.partMinutes / 60 }),
        )}
      </span>
    </li>
  )
}

function ApplyButton({ welfare }: { readonly welfare: WelfareView }) {
  const t = useT()
  const client = useClient()
  const hours = welfare.partMinutes / 60
  return (
    <div class={styles.apply}>
      <button
        type="button"
        class={styles.button}
        disabled={welfare.filling}
        onClick={() => {
          client?.welfare.apply()
        }}
      >
        {welfare.filling ? <Hourglass aria-hidden="true" /> : <Keyboard aria-hidden="true" />}
        {t(
          welfare.filling
            ? text('os.app.wellfare.filling')
            : text('os.app.wellfare.apply', { n: welfare.parts + 1 }),
        )}
      </button>
      <p class={styles.cost}>{t(text('os.app.wellfare.cost', { h: hours }))}</p>
    </div>
  )
}
